import { atom } from 'nanostores';

import { ApiError, api, type ForecastResponse } from '../lib/api';
import { isStaleSession, messageOf, onSessionCleared, refreshMe, sessionEpoch } from './auth';
import { loadUsage } from './usage';

import { track } from '../analytics/track';

export interface Sample {
  id: string;
  label: string;
  hint: string;
  values: number[];
}

/**
 * Deterministic by construction — a pseudo-random sample would make every page
 * load show a different chart, which is useless when comparing two runs.
 */
function generate(n: number, f: (t: number) => number): number[] {
  return Array.from({ length: n }, (_, t) => Number(f(t).toFixed(2)));
}

/** Shipped series, so the first run is one click and shows something legible. */
export const SAMPLES: Sample[] = [
  {
    id: 'seasonal',
    label: 'Seasonal + trend',
    hint: '96 points · 12-step cycle over a rising baseline',
    values: generate(
      96,
      (t) => 100 + 0.35 * t + 12 * Math.sin((2 * Math.PI * t) / 12) + 4 * Math.sin((2 * Math.PI * t) / 7),
    ),
  },
  {
    id: 'weekly',
    label: 'Weekly traffic',
    hint: '112 points · weekday peaks, weekend troughs',
    values: generate(
      112,
      (t) => 4200 + 900 * Math.sin((2 * Math.PI * t) / 7) - 600 * (t % 7 >= 5 ? 1 : 0) + 6 * t,
    ),
  },
  {
    id: 'growth',
    label: 'Compounding growth',
    hint: '72 points · ~4% per step, no seasonality',
    values: generate(72, (t) => 50 * Math.pow(1.04, t)),
  },
  {
    id: 'flat',
    label: 'Flat + noise',
    hint: '64 points · stationary, the boring baseline',
    values: generate(64, (t) => 500 + 18 * Math.sin(t * 2.3) + 9 * Math.cos(t * 1.1)),
  },
];

/**
 * How the playground authenticates its call.
 * - `session`: the JWT this dashboard already holds. Nothing to paste.
 * - `key`: a pasted API key, i.e. exactly what a customer integration sends.
 *
 * Both hit the same metered code path and spend the same quota; the choice is
 * about which one you want to verify.
 */
export type PlaygroundMode = 'session' | 'key';

export const $pgMode = atom<PlaygroundMode>('session');
export const $pgKey = atom('');
export const $pgSample = atom(SAMPLES[0]!.id);
export const $pgSeriesText = atom(SAMPLES[0]!.values.join(', '));
export const $pgHorizon = atom(24);
export const $pgQuantiles = atom(true);
export const $pgRunning = atom(false);
export const $pgResult = atom<ForecastResponse | null>(null);
export const $pgError = atom<string | null>(null);
export const $pgInputSeries = atom<number[]>([]);
/** Mode the displayed result was produced with, so the badge cannot lie. */
export const $pgResultMode = atom<PlaygroundMode>('session');

/**
 * Parse the textarea into a single series. Accepts commas, whitespace and
 * newlines so pasting a spreadsheet column works.
 */
export function parseSeries(text: string): { values: number[]; error: string | null } {
  const tokens = text
    .split(/[\s,;]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);

  if (tokens.length === 0) return { values: [], error: 'Paste at least 2 numbers.' };

  const values: number[] = [];
  for (const [i, tok] of tokens.entries()) {
    const n = Number(tok);
    if (!Number.isFinite(n)) {
      return { values: [], error: `Value ${i + 1} ("${tok}") is not a finite number.` };
    }
    values.push(n);
  }
  if (values.length < 2) return { values: [], error: 'A forecast needs at least 2 observations.' };
  return { values, error: null };
}

export function loadSample(id: string): void {
  const sample = SAMPLES.find((s) => s.id === id);
  if (!sample) return;
  $pgSample.set(id);
  $pgSeriesText.set(sample.values.join(', '));
}

/** The pasted key and the last result are the customer's, not the browser's. */
export function resetPlayground(): void {
  $pgMode.set('session');
  $pgKey.set('');
  $pgRunning.set(false);
  $pgResult.set(null);
  $pgError.set(null);
  $pgInputSeries.set([]);
  $pgResultMode.set('session');
}

onSessionCleared(resetPlayground);

export async function runForecast(): Promise<void> {
  const captured = sessionEpoch();
  const mode = $pgMode.get();
  const key = $pgKey.get().trim();
  if (mode === 'key' && !key) {
    $pgError.set('Paste an API key, or switch to session auth to use this dashboard’s login.');
    return;
  }
  const { values, error } = parseSeries($pgSeriesText.get());
  if (error) {
    $pgError.set(error);
    return;
  }

  const body = {
    series: [values],
    horizon: $pgHorizon.get(),
    quantiles: $pgQuantiles.get(),
  };

  $pgRunning.set(true);
  $pgError.set(null);
  try {
    const res = mode === 'key' ? await api.forecast(key, body) : await api.playgroundForecast(body);
    if (isStaleSession(captured)) return;
    $pgInputSeries.set(values);
    $pgResultMode.set(mode);
    $pgResult.set(res);
    track('playground_forecast', {
      success: true,
      series_count: values.length,
      horizon: body.horizon,
      auth: mode === 'key' ? 'api_key' : 'session',
    });
    // The call just spent quota, so the counters on every other page are stale.
    void refreshMe();
    void loadUsage(30);
  } catch (err) {
    if (isStaleSession(captured)) return;
    $pgResult.set(null);
    // The failures are the more interesting half: a first run that 429s or 422s
    // is the friction that keeps someone from ever reaching a real forecast.
    track('playground_forecast', {
      success: false,
      series_count: values.length,
      horizon: body.horizon,
      error: err instanceof ApiError ? err.code : 'unknown',
    });
    if (err instanceof ApiError && err.status === 429) {
      $pgError.set(
        `${err.message}${err.retryAfter ? ` Retry in ${err.retryAfter}s.` : ''} (rate_limit_exceeded)`,
      );
    } else if (err instanceof ApiError) {
      $pgError.set(`${err.message} (${err.code})`);
    } else {
      $pgError.set(messageOf(err));
    }
  } finally {
    $pgRunning.set(false);
  }
}
